import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  Autocomplete,
  Typography,
  CircularProgress,
} from '@mui/material'
import { SEVV_MODAL } from '../VirtualRoom/VirtualRoomModalStyles'
import InviteeSearchAutocomplete from './InviteeSearchAutocomplete'
import { loadBookableRooms } from './bookableRooms'
import { useAppSelector, useAppDispatch } from '../../hooks'
import { closeBookingForm, setLoading, triggerBookingsRefresh } from '../../stores/RoomBookingStore'
import inputController, { InputLockReason } from '../../services/InputController'
import { addNotification } from '../../stores/NotificationStore'
import Parse from '../../services/initializeParse'
import { parseCloudRun } from '../../services/parseCloud'
import type { BookingInvitee } from '../../../../types/RoomBooking'
import ExternalGuestDialog from './ExternalGuestDialog'
import {
  guestLimitReason,
  MAX_EXTERNAL_GUESTS,
  normalizeEmail,
  splitInvitees,
  toExternalInvitee,
  type ExternalGuestData,
} from './externalGuests'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import PublicIcon from '@mui/icons-material/Public'
import { REMIND_BEFORE_OPTIONS } from '../../../../types/RoomBooking'
import { listGroupHeaderSx } from '../common/ListGroupHeader'
import {
  bookingDurationError,
  endsNextDayHint,
  fitRemindBefore,
  isRemindBeforeAvailable,
  remindBeforeLabel,
  resolveBookingRange,
} from './bookingTimes'

const tzAbbr = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
  .formatToParts(new Date())
  .find((p) => p.type === 'timeZoneName')?.value || ''

/** Replace {{ISO}} tokens in an error message with local time strings */
function localizeErrorTimes(msg: string): string {
  return msg.replace(/\{\{(.+?)\}\}/g, (_match, iso) => {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) + (tzAbbr ? ` ${tzAbbr}` : '')
  })
}

/** Format a Date as YYYY-MM-DD in local time (avoids UTC shift from toISOString) */
function toLocalDateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Make native date/time picker icons visible in dark themes */
const dateTimeIconSx = {
  '& input::-webkit-calendar-picker-indicator': {
    filter: 'invert(1)',
    cursor: 'pointer',
  },
} as const

interface VirtualRoomOption {
  roomId: string
  name: string
  description?: string
  customRoomId?: string
  groupName?: string
}

export default function BookingFormDialog() {
  const dispatch = useAppDispatch()
  const { bookingFormOpen, bookingFormPreselectedRoom, bookingFormEditingId, bookingFormCompanyId, loading } = useAppSelector(
    (s) => s.roomBooking,
  )
  const companyId = useAppSelector((s) => s.room.companyId) || bookingFormCompanyId || ''
  const customRoomId = useAppSelector((s) => s.room.customRoomId) || ''
  const currentUserId = useAppSelector((s) => s.user.userData?.id) || ''

  // Form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [selectedRoom, setSelectedRoom] = useState<VirtualRoomOption | null>(null)
  const [date, setDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [invitees, setInvitees] = useState<BookingInvitee[]>([])
  const [remindBefore, setRemindBefore] = useState<number>(15)
  const [rooms, setRooms] = useState<VirtualRoomOption[]>([])
  const [roomsLoading, setRoomsLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [externalGuests, setExternalGuests] = useState<ExternalGuestData[]>([])
  const [externalGuestDialogOpen, setExternalGuestDialogOpen] = useState(false)
  const [externalGuestDialogEmail, setExternalGuestDialogEmail] = useState('')
  const [companyEmails, setCompanyEmails] = useState<Set<string>>(new Set())
  const [customRoomIdsWithReception, setCustomRoomIdsWithReception] = useState<Set<string>>(new Set())

  // Lock game input while dialog is open
  useEffect(() => {
    if (bookingFormOpen) {
      inputController.lockInput(InputLockReason.MODAL)
    } else {
      inputController.unlockInput(InputLockReason.MODAL)
    }
    return () => {
      inputController.unlockInput(InputLockReason.MODAL)
    }
  }, [bookingFormOpen])

  // Load the meeting VRs that can be booked (SC-422): in game, the current room's; on the
  // dashboard, every company room's. Each room's map owns its VRs.
  const isDashboard = !customRoomId
  useEffect(() => {
    if (!bookingFormOpen) return
    if (!companyId) return
    let cancelled = false
    setRoomsLoading(true)

    loadBookableRooms({ companyId, customRoomId: customRoomId || undefined })
      .then(({ options, customRoomIdsWithReception }) => {
        if (cancelled) return
        setCustomRoomIdsWithReception(customRoomIdsWithReception)
        setRooms(options)
        if (options.length === 0) {
          console.warn('[BookingForm] No virtual rooms found')
        }
      })
      .catch((err) => {
        console.warn('[BookingForm] Failed to load rooms:', err)
        if (!cancelled) {
          dispatch(addNotification({ type: 'error', message: 'Failed to load rooms', duration: 5000 }))
        }
      })
      .finally(() => !cancelled && setRoomsLoading(false))

    return () => { cancelled = true }
  }, [bookingFormOpen, customRoomId, companyId])

  // Pre-fill when opening
  useEffect(() => {
    if (!bookingFormOpen) return

    if (bookingFormEditingId) {
      // Edit mode: load existing booking by objectId
      const q = new Parse.Query('RoomBooking')
      q.get(bookingFormEditingId)
        .then((obj: any) => {
          if (!obj) return
          const b = {
            objectId: obj.id,
            title: obj.get('title'),
            description: obj.get('description'),
            virtualRoomId: obj.get('virtualRoomId'),
            customRoomId: obj.get('customRoomId'),
            invitees: obj.get('invitees') || [],
            remindBefore: obj.get('remindBefore'),
            startTime: obj.get('startTime'),
            endTime: obj.get('endTime'),
          }
          setTitle(b.title || '')
          setDescription(b.description || '')
          // Guests are edited in their own list; left in `invitees` they'd be re-saved as internal.
          const { internal, external } = splitInvitees(b.invitees)
          setInvitees(internal)
          setExternalGuests(external)
          setRemindBefore(b.remindBefore ?? 15)
          const start = new Date(b.startTime)
          const end = new Date(b.endTime)
          setDate(toLocalDateString(start))
          setStartTime(start.toTimeString().slice(0, 5))
          setEndTime(end.toTimeString().slice(0, 5))
          // A VR is shared by every room on its map, so the booking's room is part of its identity.
          setSelectedRoom({ roomId: b.virtualRoomId, name: b.virtualRoomId, customRoomId: b.customRoomId })
        })
        .catch((err: any) => console.warn('[BookingForm] Failed to load booking:', err))
    } else {
      // Create mode
      resetForm()
      if (bookingFormPreselectedRoom) {
        setSelectedRoom({
          roomId: bookingFormPreselectedRoom.roomId,
          name: bookingFormPreselectedRoom.roomName,
          customRoomId: customRoomId || undefined,
        })
      }
      // Default date to today, start to next rounded hour
      const now = new Date()
      setDate(toLocalDateString(now))
      const nextHour = new Date(now)
      nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0)
      setStartTime(nextHour.toTimeString().slice(0, 5))
      const endH = new Date(nextHour.getTime() + 60 * 60_000)
      setEndTime(endH.toTimeString().slice(0, 5))
    }
  }, [bookingFormOpen, bookingFormEditingId, bookingFormPreselectedRoom])

  // Match preselected/edited room to loaded rooms (resolves roomId → name)
  useEffect(() => {
    if (selectedRoom && rooms.length > 0) {
      const match = rooms.find(
        (r) => r.roomId === selectedRoom.roomId && (!selectedRoom.customRoomId || r.customRoomId === selectedRoom.customRoomId)
      )
      if (match && (match.name !== selectedRoom.name || match.customRoomId !== selectedRoom.customRoomId)) setSelectedRoom(match)
    }
  }, [rooms, selectedRoom])

  const resetForm = () => {
    setTitle('')
    setDescription('')
    setSelectedRoom(null)
    setDate('')
    setStartTime('')
    setEndTime('')
    setInvitees([])
    setExternalGuests([])
    setRemindBefore(15)
    setSubmitError(null)
  }

  const handleClose = () => {
    dispatch(closeBookingForm())
    resetForm()
  }

  const bookingRange = useMemo(() => resolveBookingRange(date, startTime, endTime), [date, startTime, endTime])

  // Ticks while open so reminder options expire even if the dialog is left sitting.
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    if (!bookingFormOpen) return
    setNowMs(Date.now())
    const id = setInterval(() => setNowMs(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [bookingFormOpen])

  const minutesUntilStart = bookingRange ? (bookingRange.start.getTime() - nowMs) / 60_000 : Infinity
  const onlyNoReminder = !isRemindBeforeAvailable(Math.min(...REMIND_BEFORE_OPTIONS.filter((m) => m > 0)), minutesUntilStart)

  useEffect(() => {
    const fitted = fitRemindBefore(remindBefore, minutesUntilStart)
    if (fitted !== remindBefore) setRemindBefore(fitted)
  }, [remindBefore, minutesUntilStart])

  const validationError = useMemo(() => {
    if (!title.trim()) return 'Title is required'
    if (!selectedRoom) return 'Please select a room'
    if (!date || !startTime || !endTime) return 'Date and time are required'

    if (!bookingRange) return 'Invalid date/time'
    const durationError = bookingDurationError(bookingRange)
    if (durationError) return durationError

    if (!bookingFormEditingId && bookingRange.start <= new Date()) return 'Start time must be in the future'
    return null
  }, [title, selectedRoom, date, startTime, endTime, bookingRange, bookingFormEditingId])

  const selectedCustomRoomId = selectedRoom?.customRoomId || customRoomId
  const externalGuestDisabledReason = !selectedRoom
    ? 'Select a meeting room first'
    : !(selectedCustomRoomId && customRoomIdsWithReception.has(selectedCustomRoomId))
      ? "This map doesn't have a reception area for external guests"
      : guestLimitReason(externalGuests.length)

  const guestEmails = useMemo(() => new Set(externalGuests.map((g) => normalizeEmail(g.email))), [externalGuests])
  const guestEmailContext = useMemo(
    () => ({
      companyEmails,
      takenEmails: new Set([...guestEmails, ...invitees.map((inv) => normalizeEmail(inv.email || '')).filter(Boolean)]),
    }),
    [companyEmails, guestEmails, invitees],
  )

  const openExternalGuestDialog = (email = '') => {
    setExternalGuestDialogEmail(email)
    setExternalGuestDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (validationError) return

    setSubmitError(null)
    dispatch(setLoading(true))
    try {
      const startISO = bookingRange!.start.toISOString()
      const endISO = bookingRange!.end.toISOString()

      // Merge internal invitees + external guests
      const allInvitees: BookingInvitee[] = [
        ...invitees,
        ...externalGuests.map(toExternalInvitee),
      ]

      const params = {
        virtualRoomId: selectedRoom!.roomId,
        customRoomId: customRoomId || selectedRoom!.customRoomId || '',
        title: title.trim(),
        description: description.trim() || undefined,
        startTime: startISO,
        endTime: endISO,
        invitees: allInvitees,
        remindBefore,
        companyId: companyId,
      }

      // parseCloudRun lets a company-closed refusal (SC-409) re-check access. No sign-out on an
      // expired token here, as before.
      if (bookingFormEditingId) {
        await parseCloudRun(
          'updateRoomBooking',
          { bookingId: bookingFormEditingId, ...params },
          { handleInvalidSession: false }
        )
        dispatch(addNotification({ type: 'success', message: 'Meeting updated', duration: 4000 }))
      } else {
        await parseCloudRun('createRoomBooking', params, { handleInvalidSession: false })
        dispatch(addNotification({ type: 'success', message: 'Meeting scheduled successfully', duration: 4000 }))
      }
      console.log('[BookingFormDialog] Booking saved, triggering refresh')
      dispatch(triggerBookingsRefresh())
      handleClose()
    } catch (err: any) {
      const message = localizeErrorTimes(err.message || 'Failed to save meeting')
      setSubmitError(message)
      dispatch(addNotification({ type: 'error', message, duration: 6000 }))
    } finally {
      dispatch(setLoading(false))
    }
  }

  return (
    <Dialog
      open={bookingFormOpen}
      onClose={(_event, reason) => { if (reason !== 'backdropClick') handleClose() }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { ...SEVV_MODAL.paper, minWidth: 480, maxWidth: 560 } }}
      slotProps={{ backdrop: { sx: SEVV_MODAL.backdrop } }}
    >
      <DialogTitle sx={SEVV_MODAL.title}>
        {bookingFormEditingId ? 'Edit Meeting' : 'Reserve a Meeting Room'}
      </DialogTitle>

      <DialogContent sx={{ ...SEVV_MODAL.content, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          fullWidth
          placeholder="e.g. Sprint Planning"
          sx={SEVV_MODAL.input}
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
          placeholder="Optional description"
          sx={SEVV_MODAL.input}
        />

        <Autocomplete
          value={selectedRoom}
          onChange={(_e, val) => setSelectedRoom(val)}
          options={rooms}
          getOptionLabel={(opt) => opt.name}
          loading={roomsLoading}
          disabled={!!bookingFormPreselectedRoom}
          isOptionEqualToValue={(opt, val) =>
            opt.roomId === val.roomId && (!val.customRoomId || opt.customRoomId === val.customRoomId)
          }
          {...(isDashboard && {
            groupBy: (opt: VirtualRoomOption) => opt.groupName || '',
            renderGroup: (params: any) => (
              <Box key={params.key} component="li">
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ ...listGroupHeaderSx, position: 'sticky', top: -8, zIndex: 1 }}
                >
                  {params.group}
                </Typography>
                {params.children}
              </Box>
            ),
          })}
          renderOption={(props, option) => {
            const { key: _labelKey, ...rest } = props as any
            // The same VR appears once per room on its map, so its name alone isn't a unique key.
            return (
              <Box component="li" key={`${option.customRoomId ?? ''}:${option.roomId}`} {...rest}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                  {option.description && (
                    <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                  )}
                </Box>
              </Box>
            )
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Room"
              required
              sx={SEVV_MODAL.input}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {roomsLoading && <CircularProgress size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: toLocalDateString(new Date()) }}
            sx={{ ...SEVV_MODAL.input, flex: 1, ...dateTimeIconSx }}
          />
          <TextField
            label="Start"
            type="time"
            value={startTime}
            onChange={(e) => {
              setStartTime(e.target.value)
              // Auto-adjust end time to +1h
              if (e.target.value) {
                const [h, m] = e.target.value.split(':').map(Number)
                const end = new Date(0, 0, 0, h + 1, m)
                setEndTime(end.toTimeString().slice(0, 5))
              }
            }}
            required
            InputLabelProps={{ shrink: true }}
            sx={{ ...SEVV_MODAL.input, flex: 1, ...dateTimeIconSx }}
          />
          <TextField
            label="End"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            required
            helperText={bookingRange?.endsNextDay && !bookingDurationError(bookingRange) ? endsNextDayHint(bookingRange.end) : undefined}
            InputLabelProps={{ shrink: true }}
            sx={{ ...SEVV_MODAL.input, flex: 1, ...dateTimeIconSx }}
          />
        </Box>
        {tzAbbr && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Times are in {tzAbbr} ({Intl.DateTimeFormat().resolvedOptions().timeZone})
          </Typography>
        )}

        <InviteeSearchAutocomplete
          value={invitees}
          onChange={setInvitees}
          companyId={companyId}
          excludeUserId={currentUserId}
          disabled={loading}
          onAddExternalGuest={openExternalGuestDialog}
          externalGuestDisabledReason={externalGuestDisabledReason}
          guestEmails={guestEmails}
          onCompanyEmailsChange={setCompanyEmails}
        />

        {/* External guest section */}
        <Box>
          <Tooltip title={externalGuestDisabledReason} arrow>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PublicIcon />}
                onClick={() => openExternalGuestDialog()}
                disabled={loading || !!externalGuestDisabledReason}
                sx={{
                  textTransform: 'none',
                  borderColor: 'var(--sevv-primary, #f97415)',
                  color: 'var(--sevv-primary, #f97415)',
                  '&:hover': { borderColor: 'var(--sevv-primary-dark, #d65c05)', bgcolor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 8%, transparent)' },
                  '&.Mui-disabled': { borderColor: 'var(--sevv-divider)', color: 'text.disabled' },
                }}
              >
                Invite External Guest
              </Button>
            </span>
          </Tooltip>
          {externalGuests.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {externalGuests.map((g, i) => (
                <Chip
                  key={`${g.email}-${i}`}
                  label={`${g.name} (${g.company}) — ${g.email}`}
                  icon={<PublicIcon sx={{ fontSize: 16 }} />}
                  size="small"
                  onDelete={() => setExternalGuests((prev) => prev.filter((_, idx) => idx !== i))}
                  sx={{ borderColor: 'var(--sevv-primary, #f97415)', color: 'text.primary' }}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Box>

        <ExternalGuestDialog
          open={externalGuestDialogOpen}
          onClose={() => setExternalGuestDialogOpen(false)}
          onAdd={(guests) => setExternalGuests((prev) => [...prev, ...guests])}
          initialEmail={externalGuestDialogEmail}
          remainingSlots={MAX_EXTERNAL_GUESTS - externalGuests.length}
          emailContext={guestEmailContext}
        />

        <TextField
          label="Remind before"
          select
          value={remindBefore}
          onChange={(e) => setRemindBefore(Number(e.target.value))}
          disabled={onlyNoReminder}
          helperText={onlyNoReminder ? 'Starts in under 5 minutes, so invitees only get the invite' : undefined}
          sx={{ ...SEVV_MODAL.input, maxWidth: onlyNoReminder ? 320 : 200 }}
        >
          {REMIND_BEFORE_OPTIONS.map((mins) => {
            const available = isRemindBeforeAvailable(mins, minutesUntilStart)
            return (
              <MenuItem key={mins} value={mins} disabled={!available}>
                {remindBeforeLabel(mins)}
                {!available && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    Meeting starts sooner
                  </Typography>
                )}
              </MenuItem>
            )
          })}
        </TextField>

        {(validationError || submitError) && (
          <Typography variant="caption" color="error">
            {validationError || submitError}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={SEVV_MODAL.actions}>
        <Button onClick={handleClose} sx={SEVV_MODAL.btnSecondary} size="small">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          sx={SEVV_MODAL.btnPrimary}
          size="small"
          disabled={!!validationError || loading}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : bookingFormEditingId ? 'Update' : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
